import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Warning as WarningIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { dataDeletionService } from '../../services/dataDeletionService';
import { useNavigate } from 'react-router-dom';

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({ open, onClose }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmIrreversible, setConfirmIrreversible] = useState(false);
  const [confirmDataLoss, setConfirmDataLoss] = useState(false);

  const requiredText = 'DELETE';
  const isConfirmationValid = confirmationText === requiredText;

  const handleDelete = async () => {
    if (!user) {
      setError('No user logged in');
      return;
    }

    if (!isConfirmationValid || !confirmDelete || !confirmIrreversible || !confirmDataLoss) {
      setError('Please complete all confirmation steps');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await dataDeletionService.deleteUserData(user.id, user.role);

      if (result.success) {
        // Sign out the user
        await signOut();
        
        // Redirect to login page
        // Use window.location for a full page reload to ensure clean state
        window.location.href = '/';
      } else {
        setError(result.message || 'Failed to delete account');
      }
    } catch (err: any) {
      console.error('Error deleting account:', err);
      setError(err.message || 'An unexpected error occurred while deleting your account');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setConfirmationText('');
      setConfirmDelete(false);
      setConfirmIrreversible(false);
      setConfirmDataLoss(false);
      setError('');
      onClose();
    }
  };

  // Check if user is a coach/headCoach with teams
  const isCoachWithTeams = user?.role === 'coach' || user?.role === 'headCoach';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="error" />
          <Typography variant="h6">Delete Account</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            This action cannot be undone
          </Typography>
        </Alert>

        <Typography variant="body2" paragraph>
          Deleting your account will permanently remove:
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <Typography component="li" variant="body2" paragraph>
            Your profile and personal information
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            All running activities and logs
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            Your goals, achievements, and progress
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            Points, badges, and rewards
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            Team memberships and relationships
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            Personal records and statistics
          </Typography>
          <Typography component="li" variant="body2" paragraph>
            Avatar customizations and inventory
          </Typography>
        </Box>

        {isCoachWithTeams && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Warning: You are a coach
            </Typography>
            <Typography variant="body2">
              If you own any teams, you must transfer ownership or delete those teams before you can delete your account.
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold' }}>
          To confirm deletion, please:
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmDelete}
                onChange={(e) => setConfirmDelete(e.target.checked)}
                disabled={loading}
              />
            }
            label="I understand that deleting my account is permanent and cannot be undone"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmIrreversible}
                onChange={(e) => setConfirmIrreversible(e.target.checked)}
                disabled={loading}
              />
            }
            label="I understand that all my data will be permanently deleted"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmDataLoss}
                onChange={(e) => setConfirmDataLoss(e.target.checked)}
                disabled={loading}
              />
            }
            label="I understand that I will lose access to all my running history, goals, and achievements"
          />
        </Box>

        <TextField
          fullWidth
          label={`Type "${requiredText}" to confirm`}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          disabled={loading}
          error={confirmationText !== '' && !isConfirmationValid}
          helperText={confirmationText !== '' && !isConfirmationValid ? `Must type exactly "${requiredText}"` : ''}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          If you have questions or need assistance, please contact us at 574-993-2331 or 
          email support before deleting your account.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={loading || !isConfirmationValid || !confirmDelete || !confirmIrreversible || !confirmDataLoss}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {loading ? 'Deleting Account...' : 'Delete My Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountDialog;
